import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../utils/axiosConfig';
import { cache, CacheKeys, CacheTTL, invalidateBookingsCache } from '../utils/cache';
import { BOOKING_TYPE_NAMES, API_BASE_URL } from '../config';
import AppShell from '../components/layout/AppShell';
import HorizontalScrollRow from '../components/calendar/HorizontalScrollRow';
import { useAuth } from '../context/AuthContext';
import { useBookingLimits } from '../hooks/useBookingLimits';
import { isBookingLimitError, getBookingLimitMessage } from '../utils/bookingLimits';
import BookingLimitBanner from '../components/ui/BookingLimitBanner';
import { hapticSuccess } from '../utils/haptic';
import { canRescheduleBooking, RESCHEDULE_LIMIT_MESSAGE } from '../utils/booking';
import AddToCalendarButton from '../components/ui/AddToCalendarButton';
import '../styles/CalendarPage.css';
import { saveLastBooking } from '../hooks/useLastBooking';
import useDocumentTitle from '../hooks/useDocumentTitle';

const PERIOD_FILTERS = [
  { id: 'any', label: 'أقرب موعد' },
  { id: 'morning', label: 'صباحاً' },
  { id: 'noon', label: 'ظهراً' },
  { id: 'afternoon', label: 'عصراً' },
];

function CalendarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const {
    can_book: canBookByLimit,
    message: bookingLimitMessage,
    daily_actions_used: dailyActionsUsed,
    daily_limit: dailyLimit,
    refreshBookingLimits,
  } = useBookingLimits(isAuthenticated);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('any');
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [selectionSource, setSelectionSource] = useState(null);
  const [showFreeTimePicker, setShowFreeTimePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timesLoading, setTimesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [successBooking, setSuccessBooking] = useState(null);
  const [bookingType, setBookingType] = useState(() => sessionStorage.getItem('selectedBookingType'));
  const [account, setAccount] = useState(null);

  const businessId = searchParams.get('businessid');
  const rescheduleId = searchParams.get('reschedule');
  const isReschedule = Boolean(rescheduleId);

  const basePageTitle = isReschedule ? 'إعادة جدولة موعد' : 'حجز موعد';
  useDocumentTitle(account?.title ? `${basePageTitle} · ${account.title}` : basePageTitle);

  const navigateWithBusinessId = (path) => {
    navigate(businessId ? `${path}?businessid=${businessId}` : path);
  };

  useEffect(() => {
    if (!businessId) {
      setAccount(null);
      return;
    }
    const cachedAccount = cache.get(CacheKeys.business(businessId));
    if (cachedAccount) {
      if (cachedAccount.can_book === false) {
        toast.error('هذا الصالون خاص ولا يمكنك الحجز فيه');
        navigate('/');
        return;
      }
      setAccount(cachedAccount);
      return;
    }
    apiClient.get(`/accounts/${businessId}`)
      .then((res) => {
        if (res.data?.success && res.data.account) {
          const acct = res.data.account;
          cache.set(CacheKeys.business(businessId), acct, CacheTTL.salons);
          if (acct.can_book === false) {
            toast.error('هذا الصالون خاص ولا يمكنك الحجز فيه');
            navigate('/');
            return;
          }
          setAccount(acct);
        } else {
          setAccount(null);
        }
      })
      .catch(() => {
        setAccount(null);
      });
  }, [businessId, navigate]);

  useEffect(() => {
    if (!isReschedule || !rescheduleId || !businessId) return;
    apiClient
      .get(`/bookings/my-bookings?businessid=${encodeURIComponent(businessId)}`)
      .then((res) => {
        const list = res.data?.bookings || [];
        const booking = list.find((b) => String(b.id) === String(rescheduleId));
        if (booking && !canRescheduleBooking(booking)) {
          toast.error(RESCHEDULE_LIMIT_MESSAGE);
          navigate(`/bookings`);
        }
      })
      .catch(() => {});
  }, [isReschedule, rescheduleId, businessId, navigate]);

  const fetchAvailableDates = async () => {
    try {
      setError('');
      const response = await apiClient.get('/calendar/available-dates');
      if (response.data.success) {
        setAvailableDates(response.data.dates || []);
      } else {
        setError('فشل في جلب التواريخ المتاحة');
        setAvailableDates([]);
      }
    } catch (err) {
      setError('لا يمكن الاتصال بالخادم. يرجى المحاولة لاحقاً.');
      setAvailableDates([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedSlots = useCallback(async (period) => {
    const currentBookingType = sessionStorage.getItem('selectedBookingType');
    if (!currentBookingType) return;

    try {
      setSuggestionsLoading(true);
      const response = await apiClient.get('/calendar/suggested-slots', {
        params: {
          reservation_type_id: currentBookingType,
          period,
          limit: 3,
        },
      });
      if (response.data.success) {
        setSuggestedSlots(response.data.slots || []);
      } else {
        setSuggestedSlots([]);
      }
    } catch (err) {
      setSuggestedSlots([]);
      console.error(err);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const fetchAvailableTimes = async (date) => {
    try {
      setTimesLoading(true);
      setError('');
      const params = { date };
      const currentBookingType = sessionStorage.getItem('selectedBookingType');
      if (currentBookingType) params.reservation_type_id = currentBookingType;
      const response = await apiClient.get('/calendar/available-times', { params });
      if (response.data.success) {
        setAvailableTimes(response.data.times || []);
      } else {
        setError('فشل في جلب الأوقات المتاحة');
        setAvailableTimes([]);
      }
    } catch (err) {
      setError('لا يمكن الاتصال بالخادم. يرجى المحاولة لاحقاً.');
      setAvailableTimes([]);
      console.error(err);
    } finally {
      setTimesLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableDates();
    const stored = sessionStorage.getItem('selectedBookingType');
    setBookingType(stored);
  }, []);

  useEffect(() => {
    if (bookingType || isReschedule) {
      fetchSuggestedSlots(periodFilter);
    }
  }, [bookingType, periodFilter, fetchSuggestedSlots, isReschedule]);

  useEffect(() => {
    if (selectedDate && showFreeTimePicker && selectionSource === 'free') {
      fetchAvailableTimes(selectedDate);
    }
  }, [selectedDate, showFreeTimePicker, selectionSource]);

  const handlePeriodChange = (periodId) => {
    setPeriodFilter(periodId);
    setSelectionSource(null);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleSuggestedSlotSelect = (slot) => {
    setSelectionSource('suggestion');
    setSelectedDate(slot.date);
    setSelectedTime(slot.time);
    setShowFreeTimePicker(false);
  };

  const handleFreeTimeDateSelect = (dateStr) => {
    setSelectionSource('free');
    setSelectedDate(dateStr);
    setSelectedTime(null);
  };

  const handleFreeTimeSelect = (time) => {
    setSelectionSource('free');
    setSelectedTime(time);
  };

  const toggleFreeTimePicker = () => {
    setShowFreeTimePicker((prev) => {
      const next = !prev;
      if (!next) {
        if (selectionSource === 'free') {
          setSelectedDate(null);
          setSelectedTime(null);
          setSelectionSource(null);
        }
      }
      return next;
    });
  };

  const isSuggestedSelected = (slot) =>
    selectionSource === 'suggestion'
    && selectedDate === slot.date
    && selectedTime === slot.time;

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) return;
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    const currentBookingType = sessionStorage.getItem('selectedBookingType');
    if (!currentBookingType && !isReschedule) {
      toast.error('يرجى اختيار نوع الحجز أولاً');
      return;
    }

    setBookingLoading(true);
    setError('');
    try {
      let response;
      if (isReschedule) {
        response = await apiClient.put(`/bookings/${rescheduleId}/reschedule`, {
          date: selectedDate,
          time: selectedTime,
          businessid: businessId,
        });
      } else {
        response = await apiClient.post('/bookings/create', {
          date: selectedDate,
          time: selectedTime,
          reservation_type_id: parseInt(currentBookingType, 10),
          businessid: businessId,
        });
      }

      if (response.data.success) {
        hapticSuccess();
        toast.success(isReschedule ? 'تم تغيير الموعد بنجاح!' : 'تم الحجز بنجاح!');
        invalidateBookingsCache();
        refreshBookingLimits();
        setSuccessBooking({
          id: response.data.booking?.id,
          date: selectedDate,
          time: selectedTime,
          booking_type: getBookingTypeName(),
          account_id: businessId,
          account_title: account?.title,
          account_address: account?.address,
        });
        setBookingSuccess(true);
        setError('');
        if (!isReschedule) {
          sessionStorage.removeItem('selectedBookingType');
          saveLastBooking({
            businessId: businessId,
            businessName: account?.title || '',
            reservationTypeId: parseInt(currentBookingType, 10),
            reservationTypeName: getBookingTypeName(),
          });
        }
      } else {
        setError(response.data.message || 'حدث خطأ');
        toast.error(response.data.message || 'حدث خطأ');
      }
    } catch (err) {
      const errType = err.response?.data?.type;
      const errorMessage = err.response?.data?.message || 'حدث خطأ في الحجز';
      if (errType === 'private_salon' || errType === 'blocked_client') {
        toast.error('هذا الصالون خاص ولا يمكنك الحجز فيه');
        sessionStorage.removeItem('selectedBookingType');
        sessionStorage.removeItem('selectedBookingTypeName');
        navigate('/');
        return;
      }
      if (isBookingLimitError(err)) {
        refreshBookingLimits();
        const limitMessage = getBookingLimitMessage(err);
        setError(limitMessage);
        toast.info(limitMessage);
        return;
      }
      setError(errorMessage);
      toast.error(errorMessage);
      console.error(err);
    } finally {
      setBookingLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
    };
  };

  const getBookingTypeName = () => {
    const storedName = sessionStorage.getItem('selectedBookingTypeName');
    if (storedName) return storedName;
    if (!bookingType) return '';
    return BOOKING_TYPE_NAMES[bookingType] || bookingType;
  };

  const backToBusiness = () => navigateWithBusinessId('/');

  const navBack = businessId
    ? {
        onClick: backToBusiness,
        label: account?.title || 'المحل',
        ariaLabel: 'العودة لصفحة المحل',
      }
    : {
        onClick: () => navigate('/'),
        label: 'ادواري',
        ariaLabel: 'العودة للصفحة الرئيسية',
      };

  const logoUrl = businessId ? `${API_BASE_URL}/files/${account?.id || businessId}` : '';

  if (loading) {
    return (
      <AppShell navBack={navBack}>
        <div className="page-container calendar-page-content">
          <p className="loading">جاري التحميل...</p>
        </div>
      </AppShell>
    );
  }

  if (!bookingType && !bookingSuccess && !isReschedule) {
    return (
      <AppShell navBack={navBack}>
        <div className="page-container calendar-page-content">
          <div className="calendar-container">
            <div className="calendar-header">
              <h1>اختر موعدك</h1>
            </div>
            <div className="error-message">يرجى اختيار نوع الحجز أولاً من صفحة المحل.</div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (bookingSuccess && successBooking) {
    const summary = formatDate(successBooking.date);
    return (
      <AppShell navBack={navBack}>
        <div className="page-container calendar-page-content">
          <div className="calendar-container booking-success-card">
            <div className="booking-success-icon" aria-hidden="true">✓</div>
            <h1 className="booking-success-title">
              {isReschedule ? 'تم تغيير الموعد!' : 'تم الحجز بنجاح!'}
            </h1>
            <p className="booking-success-summary">
              {successBooking.booking_type} — {summary.day} {summary.date} {summary.month} — {successBooking.time}
            </p>
            {account?.title && (
              <p className="booking-success-salon">{account.title}</p>
            )}
            <AddToCalendarButton
              booking={successBooking}
              salonTitle={account?.title}
              salonAddress={account?.address}
            />
            <div className="booking-success-actions">
              <button
                type="button"
                className="booking-success-btn booking-success-btn--primary"
                onClick={() => navigateWithBusinessId('/')}
              >
                العودة للمحل
              </button>
              <button
                type="button"
                className="booking-success-btn booking-success-btn--secondary"
                onClick={() => navigate(isReschedule ? '/bookings' : `/bookings`)}
              >
                حجوزاتي
              </button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  const hasSelection = Boolean(selectedDate && selectedTime);

  const selectionSummaryText = () => {
    if (!selectedDate || !selectedTime) return '';
    const f = formatDate(selectedDate);
    return `${f.day} ${f.date} ${f.month} — ${selectedTime}`;
  };

  return (
    <AppShell navBack={navBack}>
      <div className="page-container calendar-page-content">
        <div className="calendar-container">
          <div className="calendar-header">
            <div className="calendar-title-row">
              {businessId && account && (
                <img
                  src={logoUrl}
                  alt=""
                  className="calendar-header-logo"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="calendar-title-text">
                <h1>{isReschedule ? 'غيّر موعدك' : 'اختر موعدك'}</h1>
                {(bookingType || isReschedule) && (
                  <p className="booking-type">{getBookingTypeName()}</p>
                )}
              </div>
            </div>
          </div>

          {!canBookByLimit && (
            <BookingLimitBanner
              message={bookingLimitMessage}
              dailyActionsUsed={dailyActionsUsed}
              dailyLimit={dailyLimit}
            />
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="period-filter-section">
            <h2 className="period-filter-title">متى تفضّل الموعد؟</h2>
            <div className="period-filter-row">
              {PERIOD_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`period-chip ${periodFilter === f.id ? 'selected' : ''}`}
                  onClick={() => handlePeriodChange(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="suggestions-section">
            <h2>أقرب 3 مواعيد</h2>
            {suggestionsLoading ? (
              <p className="loading-times">جاري تحميل المواعيد...</p>
            ) : suggestedSlots.length > 0 ? (
              <div className="suggested-slots-row">
                {suggestedSlots.map((slot) => (
                  <button
                    key={`${slot.date}-${slot.time}`}
                    type="button"
                    className={`suggested-slot-card ${isSuggestedSelected(slot) ? 'selected' : ''}`}
                    onClick={() => handleSuggestedSlotSelect(slot)}
                  >
                    <span className="suggested-slot-label">{slot.label}</span>
                    <span className="suggested-slot-time">{slot.time}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="no-suggestions">
                لا يوجد موعد في هذه الفترة.
                جرّب فترة أخرى أو اختر وقتًا حرًا أدناه.
              </p>
            )}
          </div>

          {hasSelection && (
            <div className="selection-summary">
              الموعد المختار: {selectionSummaryText()}
            </div>
          )}

          <button
            type="button"
            className="book-btn"
            onClick={handleBooking}
            disabled={bookingLoading || !hasSelection || !canBookByLimit}
          >
            {bookingLoading
              ? (isReschedule ? 'جاري التغيير...' : 'جاري الحجز...')
              : (isReschedule ? 'تأكيد الموعد الجديد' : 'تأكيد الحجز')}
          </button>

          <div className="free-time-section">
            <button
              type="button"
              className="free-time-toggle"
              onClick={toggleFreeTimePicker}
              aria-expanded={showFreeTimePicker}
            >
              {showFreeTimePicker ? 'إخفاء اختيار الوقت الحر' : 'اختر وقتًا حرًا'}
              <span className={`free-time-chevron ${showFreeTimePicker ? 'expanded' : ''}`} aria-hidden>▼</span>
            </button>

            {showFreeTimePicker && (
              <div className="free-time-picker">
                <div className="calendar-section">
                  <h2>اختر اليوم</h2>
                  {availableDates.length > 0 ? (
                    <HorizontalScrollRow className="dates-scroll-container" scrollClassName="dates-scroll">
                      {availableDates.map((dateStr) => {
                        const formatted = formatDate(dateStr);
                        return (
                          <div
                            key={dateStr}
                            className={`date-card ${selectedDate === dateStr && selectionSource === 'free' ? 'selected' : ''}`}
                            onClick={() => handleFreeTimeDateSelect(dateStr)}
                            onKeyDown={(e) => e.key === 'Enter' && handleFreeTimeDateSelect(dateStr)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="date-day">{formatted.day}</div>
                            <div className="date-number">{formatted.date}</div>
                            <div className="date-month">{formatted.month}</div>
                          </div>
                        );
                      })}
                    </HorizontalScrollRow>
                  ) : (
                    <div className="error-message">لا توجد تواريخ متاحة</div>
                  )}
                </div>

                {selectedDate && selectionSource === 'free' && (
                  <div className="times-section">
                    <h2>اختر الوقت</h2>
                    {timesLoading ? (
                      <p className="loading-times">جاري تحميل الأوقات المتاحة...</p>
                    ) : availableTimes.length > 0 ? (
                      <HorizontalScrollRow className="times-scroll-container" scrollClassName="times-scroll">
                        {availableTimes.map((time) => (
                          <div
                            key={time}
                            className={`time-card ${selectedTime === time ? 'selected' : ''}`}
                            onClick={() => handleFreeTimeSelect(time)}
                            onKeyDown={(e) => e.key === 'Enter' && handleFreeTimeSelect(time)}
                            role="button"
                            tabIndex={0}
                          >
                            {time}
                          </div>
                        ))}
                      </HorizontalScrollRow>
                    ) : (
                      <p className="no-times">لا توجد أوقات متاحة في هذا اليوم</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

export default CalendarPage;
